import React from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import setDisplayName from 'recompose/setDisplayName';
import withProps from 'recompose/withProps';
import lifecycle from 'recompose/lifecycle';
import compose from 'recompose/compose';
import partial from 'lodash/partial';
import get from 'lodash/get';
import moment from 'moment';
import {enUSmessage} from 'i18n/en-US';
import * as ResourceApi from 'api/resources';
import {toggleSortForResourceAction} from 'viewOptions/actions';
import {getResourcePageAction, updatePageNumberForResourceAction} from 'resources/actions';
import {createResourcePageSelector, createResourcePageItemsSelector} from 'resources/selectors';
import {createResourceSortsSelector, createResourceSortOptionsSelector} from 'viewOptions/selectors';
import {prependRecentIfFirstPage, SortDirection, CurrenciesByLanguage, EmptyFunc} from 'utils/DBUtils';
import {TableViewModes, StoreStockFormModes} from '../utils';
import {
  requestStoreCatalogAction,
  submitUpdateQuantityAction,
  startWriteRetailerItemAction,
  exportStoreDataAction,
  closeExportStatusModalAction,

  requestStoreOrdersAction,
  startViewPurchaseInfoAction,
  submitResendOrderEmailAction,
  endViewPurchaseInfoAction,

  startIncreaseItemStockAction,
  submitIncreaseItemStockAction,
  startDecreaseItemStockAction,
  submitDecreaseItemStockAction,
  endUpdateItemStockAction
} from '../actions';
import {getStoreEscrow} from '../selectors';
import RewardsTableView from './RewardsTableView';

const getResourcePage = createResourcePageSelector(ResourceApi.Types.StoreCatalog);
const getPageItems = createResourcePageItemsSelector(ResourceApi.Types.StoreCatalog);
const getSorts = createResourceSortsSelector(ResourceApi.Types.StoreCatalog);
const getSortOptions = createResourceSortOptionsSelector(ResourceApi.Types.StoreCatalog);

const mapStateToProps = (state, ownProps) => {
  const {
    fetchingStoreCatalog,

    selectedLang: lang,
    selectedYear: year,
    selectedMonth: month,

    retailerItemFormMode,
    storeStockFormMode,
    updatingStoreStockFor,

    quantityUpdateErrors,
    quantityUpdateSuccesses,

    writingRetailerItem,
    writingRetailerItemResource,
    writeRetailerItemSubmitted,

    viewingPurchaseInfo,
    viewingPurchaseInfoFor,
    storeOrders,
    resendOrderEmailSubmittedFor,
    resentOrderEmailFor,

    exportingStoreData,
    exportedStoreData,
    exportStoreDataSuccess,

    allowedFutureMonths
  } = state.rewards;

  // API uses one-indexed month numbers
  const currentEscrow = getStoreEscrow({lang, year, month: month + 1})(state);
  const currentEscrowId = get(currentEscrow, 'id', null);
  const escrowExists = Boolean(currentEscrow);

  // short-circuit if there's no StoreEscrow/Catalog to be displayed.
  if (!escrowExists) {
    return {
      escrowExists
    };
  }

  const currency = CurrenciesByLanguage[lang];

  const requestedMonth = moment().month(month).year(year);
  const monthName = requestedMonth.format('MMMM');
  const now = moment();

  // the table is viewed in different modes, depending on that month's relation to now.
  const {tableMode} = [
    {
      tableMode: TableViewModes.ReadOnly,
      condition: requestedMonth.isBefore(now, 'month'),
    },
    {
      tableMode: TableViewModes.Live,
      condition: requestedMonth.isSame(now, 'month')
    },
    {
      tableMode: TableViewModes.Planning,
      condition: requestedMonth.isAfter(now, 'month')
    }
  ].find(({condition}) => condition);

  const {
    page: currentPage,
    count: pageSize,
    total: totalRewards
  } = getResourcePage(state.resources);

  const sortOptions = getSortOptions(state);
  const getSortDirectionForField = field => getSorts(state)[field] || SortDirection.Unsorted;

  const rewards = fetchingStoreCatalog
    ? []
    : prependRecentIfFirstPage(currentPage, pageSize, [], (getPageItems(state.resources) || []));

  return {
    tableMode,
    lang,
    currency,
    year,
    month,
    monthName,

    currentPage,
    pageSize,
    totalRewards,
    sortOptions,
    getSortDirectionForField,
    rewards,

    storeOrders,
    currentEscrow,
    currentEscrowId,
    escrowExists,

    quantityUpdateErrors,
    quantityUpdateSuccesses,

    retailerItemFormMode,
    writingRetailerItem,
    writingRetailerItemResource,
    writeRetailerItemSubmitted,

    viewingPurchaseInfo,
    viewingPurchaseInfoFor,
    resendOrderEmailSubmittedFor,
    resentOrderEmailFor,

    storeStockFormMode,
    updatingStoreStockFor,

    exportingStoreData,
    exportedStoreData,
    exportStoreDataSuccess,

    allowedFutureMonths
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({
  fetchStoreCatalog: requestStoreCatalogAction,
  updateQuantity: submitUpdateQuantityAction,
  openRetailerItemModal: startWriteRetailerItemAction,
  exportStoreData: exportStoreDataAction,
  closeExportStatusModal: closeExportStatusModalAction,

  getPage: page => getResourcePageAction(ResourceApi.Types.StoreCatalog, 20, page),
  onPageSelected: page => updatePageNumberForResourceAction(ResourceApi.Types.StoreCatalog, page),
  onNextClicked: currentPage => updatePageNumberForResourceAction(ResourceApi.Types.StoreCatalog, currentPage + 1),
  onPreviousClicked: currentPage => updatePageNumberForResourceAction(ResourceApi.Types.StoreCatalog, currentPage - 1),
  selectSort: field => toggleSortForResourceAction(ResourceApi.Types.StoreCatalog, field),

  openPurchaseInfoModal: startViewPurchaseInfoAction,
  getStoreOrders: requestStoreOrdersAction,
  resendOrderEmail: submitResendOrderEmailAction,
  closePurchaseInfoModal: endViewPurchaseInfoAction,

  openIncreaseItemStockModal: startIncreaseItemStockAction,
  increaseItemStock: submitIncreaseItemStockAction,

  openDecreaseItemStockModal: startDecreaseItemStockAction,
  decreaseItemStock: submitDecreaseItemStockAction,

  closeStoreStockModal: endUpdateItemStockAction
}, dispatch);

const RewardsTableViewContainer = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(props => {
    const storeStockFormSubmitters = {
      [StoreStockFormModes.Increase]: props.increaseItemStock,
      [StoreStockFormModes.Decrease]: props.decreaseItemStock
    };

    const submitStoreStockForm = partial(
      storeStockFormSubmitters[props.storeStockFormMode] || EmptyFunc,
      props.lang,
      props.currentEscrowId
    );

    return {
      submitStoreStockForm
    };
  }),
  lifecycle({
    componentWillMount() {
      if (this.props.escrowExists) {
        this.props.fetchStoreCatalog(this.props.lang, this.props.currentEscrowId, this.props.currentPage || 1);
      }
    }
  }),
  setDisplayName('RewardsTableViewContainer')
)(RewardsTableView);

export default RewardsTableViewContainer;



// WEBPACK FOOTER //
// ./public/pages/Rewards/RewardsTable/RewardsTableViewContainer.jsx